// src/pages/AddResourcePage.js

import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp, GeoPoint, getDocs } from 'firebase/firestore';
import './AddResourcePage.css';
import { useJsApiLoader } from '@react-google-maps/api';
import { useAuth } from '../context/AuthContext'; // NEW: Import useAuth hook

// --- (No changes to this component) ---
const CustomAlertModal = ({ message, onClose, onConfirm }) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleConfirm = () => {
    if (dontShowAgain) {
      localStorage.setItem('hasDismissedGpsAlert', 'true');
    }
    onConfirm();
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>GPS Coordinates Found!</h2>
        <p>{message}</p>
        <div className="modal-checkbox">
          <input
            type="checkbox"
            id="dontShowAgain"
            checked={dontShowAgain}
            onChange={(e) => setDontShowAgain(e.target.checked)}
          />
          <label htmlFor="dontShowAgain">Don't show this message again</label>
        </div>
        <div className="modal-actions">
          <button className="btn-confirm-delete" onClick={handleConfirm}>OK</button>
        </div>
      </div>
    </div>
  );
};

// --- (No changes to this component) ---
const CheckboxGroup = ({ title, options, selectedOptions, handleSelect }) => {
  return (
    <div className="checkbox-group-container">
      <p className="checkbox-group-title">{title}</p>
      <div className="checkbox-grid">
        {options.map((option) => (
          <label key={option.id} className="checkbox-label">
            <input
              type="checkbox"
              value={option.id}
              checked={selectedOptions.includes(option.id)}
              onChange={() => handleSelect(option.id)}
            />
            {option.name}
          </label>
        ))}
      </div>
    </div>
  );
};

function AddResourcePage() {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ['geocoding'],
  });

  // NEW: Get the full user object (including role) from our context
  const { currentUser } = useAuth(); 

  const [organizationName, setOrganizationName] = useState('');
  const [representativeName, setRepresentativeName] = useState('');
  const [location, setLocation] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categoriesCollection = collection(db, 'categories');
        const categorySnapshot = await getDocs(categoriesCollection);
        const categoryList = categorySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setCategories(categoryList);
      } catch (error) {
        console.error("Error fetching categories: ", error);
        alert("Could not load resource categories. Please try refreshing the page.");
      }
    };

    fetchCategories();
  }, []);

  const handleCategoryChange = (categoryId) => {
    setSelectedCategoryIds(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleCalculateGps = () => {
    if (!location || location.trim() === '') {
      alert('Please enter a location address before calculating GPS.');
      return;
    }
    
    setIsCalculating(true);
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: location }, (results, status) => {
      setIsCalculating(false);
      if (status === 'OK') {
        const geo = results[0].geometry.location;
        const lat = geo.lat();
        const lng = geo.lng();
        setLatitude(lat);
        setLongitude(lng);
        
        const hasDismissed = localStorage.getItem('hasDismissedGpsAlert') === 'true';
        if (!hasDismissed) {
          setAlertMessage(`The fields have been filled for you.\nLatitude: ${lat}\nLongitude: ${lng}\n\nPlease remember to click "Save Resource" to finish.`);
          setShowAlert(true);
        }

      } else {
        alert('Geocode was not successful for the following reason: ' + status);
      }
    });
  };

  // --- UPDATED: This function now adds the `status` field ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    // UPDATED: Check for the user from our context
    if (!currentUser) {
        alert("A user was not found, please try logging out and signing back in.");
        return;
    }
    if (selectedCategoryIds.length === 0) {
      alert("Please select at least one 'Type of Resource'.");
      return;
    }
    setIsSubmitting(true);
    try {
      const resourceData = {
        organizationName,
        representativeName,
        location,
        couponCode,
        createdBy: currentUser.uid, // Use uid from context user
        createdAt: serverTimestamp(),
        categoryIDs: selectedCategoryIds,
        // NEW: Set status based on the user's role
        status: currentUser.role === 'admin' ? 'approved' : 'pending',
      };

      if (latitude && longitude) {
        resourceData.coordinates = new GeoPoint(parseFloat(latitude), parseFloat(longitude));
      }

      await addDoc(collection(db, "resources"), resourceData);
      alert('Resource saved successfully!');
      setOrganizationName('');
      setRepresentativeName('');
      setSelectedCategoryIds([]);
      setLocation('');
      setLatitude('');
      setLongitude('');
      setCouponCode('');
    } catch (error) {
      console.error("Error adding document: ", error);
      alert(`Error saving resource. Please check the console for details. Error: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const getGpsButtonText = () => {
    if (!isLoaded) return 'Still Loading...';
    if (isCalculating) return 'Calculating...';
    return 'Calculate GPS from Address';
  };
  
  if (loadError) {
    return <div>Error loading maps. Please check the API key and console for details.</div>;
  }

  return (
    <div className="page-container">
      {showAlert && <CustomAlertModal message={alertMessage} onConfirm={() => setShowAlert(false)} />}
      <div className="form-container">
        <h1 className="form-header">Add a New Resource</h1>
        <form onSubmit={handleSubmit}>
          
          <div className="form-group">
            <label htmlFor="organizationName" className="form-label">Name of your Organization</label>
            <input type="text" id="organizationName" value={organizationName} onChange={(e) => setOrganizationName(e.target.value)} className="form-input" placeholder="e.g., The Community Center" required />
          </div>

          <div className="form-group">
            <label htmlFor="representativeName" className="form-label">Name of the Representative</label>
            <input type="text" id="representativeName" value={representativeName} onChange={(e) => setRepresentativeName(e.target.value)} className="form-input" placeholder="e.g., Jane Doe" required />
          </div>

          <div className="form-group">
            <CheckboxGroup
              title="Type of Resource (select all that apply)"
              options={categories}
              selectedOptions={selectedCategoryIds}
              handleSelect={handleCategoryChange}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="location" className="form-label">Location Address</label>
            <input type="text" id="location" value={location} onChange={(e) => setLocation(e.target.value)} className="form-input" placeholder="e.g., 123 Main St, Anytown, USA" required/>
          </div>

          {!latitude && !longitude && (
            <div className="form-group-action">
              <button type="button" onClick={handleCalculateGps} className="calculate-gps-button" disabled={!isLoaded || isCalculating}>
                {getGpsButtonText()}
              </button>
            </div>
          )}

          <p className="form-label-optional">Or, enter GPS coordinates directly below.</p>
          <div className="coordinate-group">
            <div className="form-group">
              <label htmlFor="latitude" className="form-label">Latitude</label>
              <input type="number" step="any" id="latitude" value={latitude} onChange={(e) => setLatitude(e.target.value)} className="form-input" placeholder="e.g., 40.7128" />
            </div>
            <div className="form-group">
              <label htmlFor="longitude" className="form-label">Longitude</label>
              <input type="number" step="any" id="longitude" value={longitude} onChange={(e) => setLongitude(e.target.value)} className="form-input" placeholder="e.g., -74.0060" />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="couponCode" className="form-label">Coupon Code (Optional)</label>
            <input type="text" id="couponCode" value={couponCode} onChange={(e) => setCouponCode(e.target.value)} className="form-input" placeholder="e.g., SAVE20, 1234-5678" />
          </div>

          <button type="submit" className="submit-button" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save Resource'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default AddResourcePage;